import {
  type CSSProperties,
  type FC,
  type PointerEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type ScrollCustomEvent, useIonRouter } from "@ionic/react";
import { useHistory } from "react-router-dom";
import { type TFunction } from "i18next";
import { observer } from "mobx-react-lite";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useTranslation } from "react-i18next";
import {
  Activity,
  ArrowRight,
  ArrowUpRight,
  ChevronRight,
  Dumbbell,
  ListChecks,
} from "lucide-react";
import { Content, DefaultPage } from "@shared/layout/DefaultPage";
import { AppNavbar } from "@shared/ui/AppNavbar";
import { PwaInstallationModal } from "@widgets/PwaInstallationModal";
import { ProgramDataProxy } from "@entities/program/api/data.proxy";
import {
  type ProgramEnrollment,
  type ProgramTemplate,
} from "@entities/program/types/template";
import { getWeeksAmount } from "@entities/program/lib/weeksAmount";
import {
  type Training,
  type WorkoutType,
  getWorkoutTypeProfile,
  TrainingsProxy,
  trainingQueryKeys,
} from "@entities/training";
import { getMediaUrl } from "@shared/helpers/getMediaUrl";
import { ROUTES } from "@shared/constants/routes";
import { getCurrentBackendLocale } from "@shared/i18n";

const getProgramTitle = (
  program: ProgramTemplate | null | undefined,
): string => {
  return program?.program_name?.trim() || program?.name?.trim() || "";
};

const getProgramDescription = (
  program: ProgramTemplate | null | undefined,
): string => {
  return (
    program?.program_description?.trim() || program?.description?.trim() || ""
  );
};

const getProgramWeeks = (
  program: ProgramTemplate | null | undefined,
): number => {
  return program?.weeks_count || getWeeksAmount(program) || 0;
};

const getProgramProgress = (
  program: ProgramTemplate | null | undefined,
): number => {
  const progress = program?.statistics?.progress_percentage ?? 0;

  return Math.min(100, Math.max(0, Math.round(progress)));
};

const formatRuCount = (
  value: number,
  forms: [string, string, string],
): string => {
  const mod10 = value % 10;
  const mod100 = value % 100;

  if (mod10 === 1 && mod100 !== 11) {
    return `${value} ${forms[0]}`;
  }

  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
    return `${value} ${forms[1]}`;
  }

  return `${value} ${forms[2]}`;
};

const getHeroDescriptionLines = (description: string): string[] => {
  const normalizedDescription = description.replace(/\s+/g, " ").trim();
  const statsMatch = normalizedDescription.match(
    /^Срок:\s*(.+?)\.\s*Тренировок в неделю:\s*(\d+)\.?$/i,
  );

  if (statsMatch) {
    return [
      `${statsMatch[1]} программы`,
      `${formatRuCount(Number(statsMatch[2]), ["тренировка", "тренировки", "тренировок"])} в неделю`,
    ];
  }

  return normalizedDescription ? [normalizedDescription] : [];
};

const getCurrentWeek = (progress: number, weeksAmount: number): number => {
  if (weeksAmount <= 0) {
    return 0;
  }

  if (progress <= 0) {
    return 1;
  }

  return Math.min(
    weeksAmount,
    Math.max(1, Math.ceil((progress / 100) * weeksAmount)),
  );
};

const formatCompactExerciseCount = (count: number, t: TFunction): string => {
  if (count === 0) {
    return t("main:workouts.noExercises");
  }

  return t("main:workouts.exerciseCountCompact", { count });
};

const getWorkoutMetaLabel = (training: Training, t: TFunction): string => {
  const exerciseCount = training.exercises?.length ?? 0;
  const history = training.history;

  if (
    history?.status === "in_progress" &&
    history.exercises_total > 0 &&
    history.exercises_completed > 0
  ) {
    const completedExercises = Math.min(
      history.exercises_completed,
      history.exercises_total,
    );

    return t("main:workouts.doneProgress", {
      completed: completedExercises,
      total: history.exercises_total,
    });
  }

  return formatCompactExerciseCount(exerciseCount, t);
};

const getWorkoutTitle = (training: Training, t: TFunction): string => {
  return training.name?.trim() || t("main:workouts.untitled");
};

type WorkoutTypeBadgeCode = Exclude<WorkoutType, "mixed">;

const getWorkoutTypeBadgeCodes = (
  training: Training,
): WorkoutTypeBadgeCode[] => {
  const workoutType = getWorkoutTypeProfile(training).workout_type;

  if (workoutType === "mixed") {
    return ["strength", "cardio"];
  }

  if (workoutType === "strength" || workoutType === "cardio") {
    return [workoutType];
  }

  return [];
};

const getWorkoutTypeBadgeLabel = (
  badgeCode: WorkoutTypeBadgeCode,
  t: TFunction,
): string => {
  return badgeCode === "cardio"
    ? t("main:workouts.typeCardio")
    : t("main:workouts.typeStrength");
};

const WORKOUT_MASCOT_GIF_URL =
  "https://media.tenor.com/6fpC80BmM88AAAAM/fall-out.gif";
const HOME_LOGO_TAP_MESSAGES = [
  "да-да, привет",
  "готов заниматься?",
  "приятно ^^",
  "ну все, давай уже к тренировкам",
] as const;
const ENROLLMENT_PREFETCH_STALE_TIME_MS = 30 * 1000;

interface SectionTitleProps {
  title: string;
  action?: string;
  onAction?: () => void;
}

const SectionTitle: FC<SectionTitleProps> = ({ title, action, onAction }) => (
  <div className="mb-3 flex items-center justify-between gap-3">
    <h2 className="text-[20px] font-bold leading-none text-[#101113]">
      {title}
    </h2>
    {action && (
      <button
        type="button"
        onClick={onAction}
        className="inline-flex min-h-11 items-center gap-2 rounded-[14px] px-1 text-[14px] font-medium text-[#737373]"
      >
        {action}
        <ChevronRight className="size-[18px]" aria-hidden="true" />
      </button>
    )}
  </div>
);

interface HomeHeroProps {
  program: ProgramTemplate | null | undefined;
  isLoading: boolean;
  onOpen: () => void;
  t: TFunction;
}

const HomeHero: FC<HomeHeroProps> = ({ program, isLoading, onOpen, t }) => {
  if (isLoading) {
    return (
      <div className="h-[394px] rounded-[28px] bg-[#F0F1EF] p-6">
        <div className="h-4 w-32 animate-pulse rounded bg-white/80" />
        <div className="mt-7 h-20 w-44 animate-pulse rounded-[14px] bg-white/80" />
        <div className="mt-5 h-11 w-36 animate-pulse rounded-[12px] bg-white/70" />
        <div className="mt-auto h-[58px] w-full animate-pulse rounded-[18px] bg-white/80" />
      </div>
    );
  }

  if (!program) {
    return null;
  }

  const coverUrl = getMediaUrl(program.media?.cover);
  const title = getProgramTitle(program);
  const description = getProgramDescription(program);
  const descriptionLines = getHeroDescriptionLines(description);
  const weeksAmount = getProgramWeeks(program);
  const progress = getProgramProgress(program);
  const currentWeek = getCurrentWeek(progress, weeksAmount);
  const ctaTitle = program.enrollment_id
    ? t("main:hero.ctaContinue")
    : t("main:hero.ctaStart");

  return (
    <div
      style={{
        transitionTimingFunction: "cubic-bezier(0.32, 0.72, 0, 1)",
      }}
      className="relative h-[374px] w-full overflow-hidden rounded-[32px] bg-[#F7F8F4] p-[22px] text-left transition-transform duration-500"
    >
      {coverUrl && (
        <div className="absolute inset-0 overflow-hidden rounded-[inherit]">
          <div
            className="absolute bottom-0 left-[38%] right-[-42%] top-0"
            style={{
              WebkitMaskImage:
                "linear-gradient(112deg, transparent 0%, transparent 18%, black 48%, black 100%)",
              maskImage:
                "linear-gradient(112deg, transparent 0%, transparent 18%, black 48%, black 100%)",
            }}
          >
            <img
              src={coverUrl}
              alt=""
              className="h-full w-full object-cover object-[90%_center] opacity-100"
            />
          </div>
          <div className="absolute inset-x-0 bottom-0 h-[16%] bg-[linear-gradient(180deg,rgba(247,248,244,0)_0%,rgba(247,248,244,0.82)_100%)]" />
        </div>
      )}

      <div className="relative z-10 flex h-full flex-col pb-[78px]">
        <p className="text-[13px] font-semibold uppercase leading-none tracking-[0.12em] text-[#7D858B]">
          {t("main:hero.currentProgram")}
        </p>
        {title && (
          <h1 className="mt-[18px] max-w-[286px] text-[30px] font-extrabold leading-[1.03] text-[#101113]">
            {title}
          </h1>
        )}
        {descriptionLines.length > 0 && (
          <p className="mt-4 max-w-[225px] text-[15px] font-medium leading-[1.4] text-[#34383B]">
            {descriptionLines.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </p>
        )}

        <div className="mt-auto">
          {weeksAmount > 0 && (
            <>
              <div className="mb-3 flex w-[252px] max-w-full items-center justify-between text-[16px] font-bold text-[#121416]">
                <span>
                  {t("main:hero.weekProgress", {
                    current: currentWeek,
                    total: weeksAmount,
                  })}
                </span>
                <span className="tabular-nums">{progress}%</span>
              </div>
              <div className="h-2 w-[252px] max-w-full overflow-hidden rounded-full bg-[#DADDE0]/80">
                <div
                  className="h-full rounded-full bg-[#D7FB36]"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </>
          )}
        </div>
      </div>
      <div className="absolute inset-x-[22px] bottom-[22px] z-20">
        <button
          type="button"
          onClick={onOpen}
          className="group relative grid h-[58px] w-full overflow-hidden rounded-[24px] bg-transparent text-left shadow-[0_12px_22px_rgba(16,17,19,0.13)] transition-transform duration-200 [-webkit-tap-highlight-color:transparent] active:scale-[0.985] focus:outline-none focus-visible:outline-none"
        >
          <span
            className="cta-shooting-star absolute inset-0 rounded-[inherit]"
            aria-hidden="true"
          />
          <span className="absolute inset-[2px] rounded-[22px] bg-[#101113] transition-colors duration-200 group-active:bg-[#181A1C]" />
          <span className="relative z-10 flex h-full min-w-0 items-center justify-center gap-3 px-[18px] text-[16px] font-semibold">
            <span className="min-w-0 truncate text-[#F7F8F4]">
              {ctaTitle}
            </span>
            <ArrowRight
              className="size-[27px] shrink-0"
              color="#F7F8F4"
              strokeWidth={1.9}
              aria-hidden="true"
            />
          </span>
        </button>
      </div>
    </div>
  );
};

interface WorkoutCardShellProps {
  title: string;
  metaLabel: string;
  typeBadgeCodes: WorkoutTypeBadgeCode[];
  coverUrl?: string;
  onOpen?: () => void;
  t: TFunction;
}

const WorkoutCardShell: FC<WorkoutCardShellProps> = ({
  title,
  metaLabel,
  typeBadgeCodes,
  coverUrl,
  onOpen,
  t,
}) => (
  <button
    type="button"
    onClick={onOpen}
    disabled={!onOpen}
    style={{
      transitionTimingFunction: "cubic-bezier(0.23, 1, 0.32, 1)",
    }}
    className="group grid h-[168px] min-w-0 grid-cols-[108px_minmax(0,1fr)] overflow-hidden rounded-[24px] bg-[#F1F2F1] text-left ring-1 ring-black/[0.025] transition-transform duration-150 [-webkit-tap-highlight-color:transparent] active:scale-[0.985]"
  >
    <div className="relative h-full overflow-hidden rounded-l-[24px] bg-[#E9EBE8]">
      {coverUrl ? (
        <img
          src={coverUrl}
          alt=""
          className="h-full w-full object-cover object-[35%_center] motion-safe:transition-transform motion-safe:duration-200 group-active:scale-[1.025]"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-[linear-gradient(145deg,#E7EAE6_0%,#F4F5F3_100%)] text-[#A8AEAA]">
          <Dumbbell className="size-10" strokeWidth={1.8} aria-hidden="true" />
        </div>
      )}

      <div className="absolute left-2 top-2 z-10 flex items-center">
        {typeBadgeCodes.map((badgeCode, index) => {
          const Icon = badgeCode === "cardio" ? Activity : Dumbbell;

          return (
            <span
              key={badgeCode}
              aria-label={getWorkoutTypeBadgeLabel(badgeCode, t)}
              title={getWorkoutTypeBadgeLabel(badgeCode, t)}
              style={{ zIndex: index + 1 }}
              className={`inline-flex size-[34px] items-center justify-center rounded-full bg-white/[0.95] text-[#15171A] shadow-[0_8px_20px_rgba(15,17,19,0.13)] ${
                index > 0 ? "-ml-2.5" : ""
              }`}
            >
              <Icon
                className="size-[17px]"
                strokeWidth={2.25}
                aria-hidden="true"
              />
            </span>
          );
        })}
      </div>
    </div>

    <div className="flex h-full min-w-0 flex-col justify-between px-4 pb-4 pt-[18px]">
      <div className="min-w-0">
        <h3 className="line-clamp-3 text-[18px] font-extrabold leading-[1.08] text-[#111214]">
          {title}
        </h3>
      </div>
      <div className="inline-flex min-w-0 items-center gap-1.5 text-[13px] font-bold leading-none text-[#202326]">
        <ListChecks
          className="size-[15px] shrink-0"
          strokeWidth={2.25}
          aria-hidden="true"
        />
        <span className="whitespace-nowrap">{metaLabel}</span>
      </div>
    </div>
  </button>
);

interface WorkoutCardProps {
  training: Training;
  onOpen: (training: Training) => void;
  t: TFunction;
}

const WorkoutCard: FC<WorkoutCardProps> = ({ training, onOpen, t }) => {
  const coverUrl = getMediaUrl(training.media?.cover);
  const metaLabel = getWorkoutMetaLabel(training, t);
  const typeBadgeCodes = getWorkoutTypeBadgeCodes(training);

  return (
    <WorkoutCardShell
      title={getWorkoutTitle(training, t)}
      metaLabel={metaLabel}
      typeBadgeCodes={typeBadgeCodes}
      coverUrl={coverUrl}
      onOpen={() => onOpen(training)}
      t={t}
    />
  );
};

interface ProgramRowCardProps {
  program: ProgramTemplate;
  onOpen: () => void;
  t: TFunction;
}

const ProgramRowCard: FC<ProgramRowCardProps> = ({ program, onOpen, t }) => {
  const coverUrl = getMediaUrl(program.media?.cover);
  const title = getProgramTitle(program);
  const weeksAmount = getProgramWeeks(program);
  const progress = getProgramProgress(program);
  const hasProgress = Boolean(program.enrollment_id && program.statistics);

  return (
    <button
      type="button"
      onClick={onOpen}
      className="grid h-[118px] w-full grid-cols-[34%_1fr] overflow-hidden rounded-[20px] bg-white text-left ring-1 ring-black/[0.035]"
    >
      {coverUrl ? (
        <img
          src={coverUrl}
          alt=""
          className="h-full w-full bg-[#F1F2F1] object-cover object-center"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-[#F1F2F1] text-[#B0B4B2]">
          <Dumbbell className="size-9" strokeWidth={1.8} aria-hidden="true" />
        </div>
      )}
      <div className="flex h-full min-w-0 flex-col justify-between px-5 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="truncate text-[18px] font-bold leading-tight text-[#111214]">
              {title}
            </h3>
            {weeksAmount > 0 && (
              <p className="mt-1 text-[14px] font-medium leading-none text-[#777B80]">
                {t("main:programs.weeksProgram", { count: weeksAmount })}
              </p>
            )}
          </div>
          <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-[12px] bg-[#F7FDE7] text-[#111214]">
            <ArrowUpRight className="size-[21px]" aria-hidden="true" />
          </span>
        </div>

        {hasProgress ? (
          <div className="flex items-center gap-4">
            <div className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-[#D9DADC]">
              <div
                className="h-full rounded-full bg-[#D7FB36]"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="w-10 text-right text-[15px] font-semibold tabular-nums text-[#101113]">
              {progress}%
            </span>
          </div>
        ) : (
          <div className="inline-flex h-8 w-fit items-center rounded-full bg-[#101113] px-4 text-[13px] font-semibold text-white">
            {t("common:actions.start")}
          </div>
        )}
      </div>
    </button>
  );
};

interface ProgramSectionProps {
  title: string;
  programs: ProgramTemplate[];
  isLoading: boolean;
  emptyLabel?: string;
  onProgramOpen: (program: ProgramTemplate) => void;
  t: TFunction;
}

const ProgramSection: FC<ProgramSectionProps> = ({
  title,
  programs,
  isLoading,
  emptyLabel,
  onProgramOpen,
  t,
}) => (
  <section>
    <SectionTitle title={title} />
    {isLoading && (
      <div className="flex flex-col gap-4">
        <div className="h-[118px] animate-pulse rounded-[20px] bg-white" />
        <div className="h-[118px] animate-pulse rounded-[20px] bg-white" />
      </div>
    )}
    {!isLoading && programs.length > 0 && (
      <div className="scroll-reveal-list flex flex-col gap-4">
        {programs.map((program) => (
          <ProgramRowCard
            key={`${program.enrollment_id ?? "template"}-${program.program_template_id}`}
            program={program}
            t={t}
            onOpen={() => onProgramOpen(program)}
          />
        ))}
      </div>
    )}
    {!isLoading && programs.length === 0 && emptyLabel && (
      <div className="flex h-[96px] w-full items-center justify-center rounded-[20px] bg-white text-[15px] font-semibold text-[#555A5F] ring-1 ring-black/[0.035]">
        {emptyLabel}
      </div>
    )}
  </section>
);

export const MainPage = observer(() => {
  const nav = useIonRouter();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { t } = useTranslation(["main", "common"]);
  const backendLocale = getCurrentBackendLocale();
  const prefersReducedMotion = useReducedMotion();
  const [isWorkoutMascotVisible, setIsWorkoutMascotVisible] = useState(false);
  const contentRef = useRef<HTMLIonContentElement | null>(null);
  const workoutsSectionRef = useRef<HTMLElement | null>(null);
  const homeHeaderRef = useRef<HTMLElement | null>(null);
  const homeHeaderProgressRef = useRef(0);
  const programOpenInFlightRef = useRef(false);
  const workoutGestureStartX = useRef<number | null>(null);
  const workoutGestureStartY = useRef<number | null>(null);
  const workoutMascotTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const {
    data: startedProgramTemplates,
    isLoading: isStartedProgramTemplatesLoading,
  } = useQuery({
    queryKey: ["main_page", "programs", "started", backendLocale],
    queryFn: async () =>
      ProgramDataProxy.getProgramsList({
        limit: 6,
        offset: 0,
        lang: backendLocale,
        type: "enrollments",
      }),
  });

  const {
    data: availableProgramTemplates,
    isLoading: isAvailableProgramTemplatesLoading,
  } = useQuery({
    queryKey: ["main_page", "programs", "templates", backendLocale],
    queryFn: async () =>
      ProgramDataProxy.getProgramsList({
        limit: 6,
        offset: 0,
        lang: backendLocale,
        type: "available",
      }),
  });

  const { data: trainings, isLoading: isTrainingsLoading } = useQuery({
    queryKey: trainingQueryKeys.homePreview(backendLocale),
    queryFn: async () =>
      TrainingsProxy.getTrainings({
        start: 0,
        limit: 6,
      }),
  });

  const startedPrograms = startedProgramTemplates ?? [];
  const availablePrograms = availableProgramTemplates ?? [];
  const workoutItems = trainings ?? [];
  const startedProgramPreviewItems = startedPrograms
    .filter((program) => getProgramTitle(program))
    .slice(0, 2);
  const availableProgramPreviewItems = availablePrograms
    .filter((program) => getProgramTitle(program))
    .slice(0, 4);
  const workoutPreviewItems = workoutItems.slice(0, 3);
  const suggestedProgram =
    startedProgramPreviewItems[0] ?? availableProgramPreviewItems[0] ?? null;
  const isSuggestedProgramLoading =
    isStartedProgramTemplatesLoading ||
    (!startedProgramPreviewItems.length && isAvailableProgramTemplatesLoading);
  const shouldShowStartedPrograms =
    isStartedProgramTemplatesLoading || startedProgramPreviewItems.length > 0;
  const shouldShowAvailablePrograms =
    isAvailableProgramTemplatesLoading ||
    availableProgramPreviewItems.length > 0 ||
    (!isStartedProgramTemplatesLoading &&
      startedProgramPreviewItems.length === 0);

  useEffect(() => {
    (startedProgramTemplates ?? []).forEach((program) => {
      if (!program.enrollment_id) {
        return;
      }

      const enrollmentId = program.enrollment_id;
      void queryClient.prefetchQuery({
        queryKey: ["enrollment", String(enrollmentId)],
        queryFn: () => ProgramDataProxy.getEnrollmentById(enrollmentId),
        staleTime: ENROLLMENT_PREFETCH_STALE_TIME_MS,
      });
    });
  }, [queryClient, startedProgramTemplates]);

  useEffect(() => {
    return () => {
      if (workoutMascotTimerRef.current) {
        clearTimeout(workoutMascotTimerRef.current);
      }
    };
  }, []);

  const handleProgramOpen = async (
    program: ProgramTemplate | null | undefined,
  ) => {
    if (!program) {
      nav.push(ROUTES.overall.trainingConstructor);
      return;
    }

    if (program.enrollment_id) {
      const cachedEnrollment =
        queryClient.getQueryData<ProgramEnrollment | null>([
          "enrollment",
          String(program.enrollment_id),
        ]);

      if (cachedEnrollment?.pointer_week) {
        nav.push(
          `/program/${program.enrollment_id}/week/${cachedEnrollment.pointer_week}`,
        );
        return;
      }

      nav.push(`/program/${program.enrollment_id}`);
      return;
    }

    if (programOpenInFlightRef.current) {
      return;
    }

    programOpenInFlightRef.current = true;

    try {
      const enrollment = await ProgramDataProxy.enroll(
        program.program_template_id,
      );
      if (enrollment) {
        const targetWeek =
          enrollment.pointer_week || enrollment.current_week_num || 1;
        nav.push(
          `/program/${enrollment.id_program_enrollment}/week/${targetWeek}`,
        );
      }
    } finally {
      programOpenInFlightRef.current = false;
    }
  };

  const showWorkoutMascot = () => {
    if (workoutMascotTimerRef.current) {
      clearTimeout(workoutMascotTimerRef.current);
    }

    setIsWorkoutMascotVisible(true);
    workoutMascotTimerRef.current = setTimeout(() => {
      setIsWorkoutMascotVisible(false);
    }, 1600);
  };

  const handleLogoEasterEggComplete = () => {
    workoutsSectionRef.current?.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "start",
    });
    showWorkoutMascot();
  };

  const handleHomeLogoClick = () => {
    if (homeHeaderProgressRef.current <= 0.05) {
      return false;
    }

    void contentRef.current?.scrollToTop(prefersReducedMotion ? 0 : 280);
    return true;
  };

  const resetWorkoutGesture = () => {
    workoutGestureStartX.current = null;
    workoutGestureStartY.current = null;
  };

  const openWorkoutDetails = (training: Training) => {
    history.push({
      pathname: ROUTES.training.details.replace(
        ":trainingID",
        String(training.id_workout),
      ),
      state: {
        trainingDetailsReturnTo: ROUTES.overall.main,
      },
    });
  };

  const handleHomeScroll = (event: ScrollCustomEvent) => {
    const progress = Math.min(1, Math.max(0, event.detail.scrollTop / 32));

    if (Math.abs(progress - homeHeaderProgressRef.current) < 0.015) {
      return;
    }

    const easedProgress = 1 - Math.pow(1 - progress, 2);
    homeHeaderProgressRef.current = progress;
    homeHeaderRef.current?.style.setProperty(
      "--home-header-icon-y",
      `${easedProgress * -8}px`,
    );
    homeHeaderRef.current?.style.setProperty(
      "--home-header-logo-x",
      `${easedProgress * 8}px`,
    );
    homeHeaderRef.current?.style.setProperty(
      "--home-header-avatar-x",
      `${easedProgress * -8}px`,
    );
    homeHeaderRef.current?.style.setProperty(
      "--home-header-icon-scale",
      `${1 - easedProgress * 0.09}`,
    );
    homeHeaderRef.current?.style.setProperty(
      "--home-header-icon-opacity",
      `${1 - easedProgress * 0.45}`,
    );
  };

  const handleWorkoutsPointerDown = (event: PointerEvent<HTMLElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }

    workoutGestureStartX.current = event.clientX;
    workoutGestureStartY.current = event.clientY;
  };

  const handleWorkoutsPointerMove = (event: PointerEvent<HTMLElement>) => {
    if (
      workoutGestureStartX.current === null ||
      workoutGestureStartY.current === null
    ) {
      return;
    }

    const deltaX = event.clientX - workoutGestureStartX.current;
    const deltaY = event.clientY - workoutGestureStartY.current;
    const isRightEdgeSwipe =
      deltaX < -44 && Math.abs(deltaX) > Math.abs(deltaY) * 1.4;

    if (!isRightEdgeSwipe) {
      return;
    }

    resetWorkoutGesture();
    showWorkoutMascot();
  };

  return (
    <DefaultPage id="main-content" noPadding style={{ background: "#FBFBFA" }}>
      <PwaInstallationModal />
      <Content
        ref={contentRef}
        scrollEvents
        onIonScroll={handleHomeScroll}
        style={{ "--background": "#FBFBFA" } as CSSProperties}
      >
        <main className="mx-auto flex min-h-full w-full max-w-[500px] flex-col gap-7 bg-[#FBFBFA] px-5 pb-[var(--app-bottom-nav-reserve)]">
          <AppNavbar
            headerRef={homeHeaderRef}
            logoAriaLabel="Пасхалка TISIT"
            onLogoClick={handleHomeLogoClick}
            logoTap={{
              messages: HOME_LOGO_TAP_MESSAGES,
              onComplete: handleLogoEasterEggComplete,
            }}
            className="-mx-5"
            style={
              {
                "--home-header-icon-y": "0px",
                "--home-header-logo-x": "0px",
                "--home-header-avatar-x": "0px",
                "--home-header-icon-scale": 1,
                "--home-header-icon-opacity": 1,
              } as CSSProperties
            }
            logoContainerClassName="will-change-[transform,opacity]"
            logoContainerStyle={{
              opacity: "var(--home-header-icon-opacity)",
              transform:
                "translate3d(var(--home-header-logo-x), var(--home-header-icon-y), 0) scale(var(--home-header-icon-scale))",
              transformOrigin: "center",
            }}
            avatarStyle={{
              opacity: "var(--home-header-icon-opacity)",
              transform:
                "translate3d(var(--home-header-avatar-x), var(--home-header-icon-y), 0) scale(var(--home-header-icon-scale))",
              transformOrigin: "center",
            }}
          />

          <HomeHero
            program={suggestedProgram}
            isLoading={isSuggestedProgramLoading}
            t={t}
            onOpen={() => handleProgramOpen(suggestedProgram)}
          />

          <section
            ref={workoutsSectionRef}
            className="relative -mx-5 overflow-hidden px-5 [touch-action:pan-y]"
            onPointerDown={handleWorkoutsPointerDown}
            onPointerMove={handleWorkoutsPointerMove}
            onPointerUp={resetWorkoutGesture}
            onPointerCancel={resetWorkoutGesture}
          >
            {(isTrainingsLoading || workoutPreviewItems.length > 0) && (
              <SectionTitle
                title={t("main:workouts.title")}
                action={t("main:workouts.viewAll")}
                onAction={() =>
                  nav.push(
                    `${ROUTES.overall.trainingConstructor}?tab=my_trainings`,
                  )
                }
              />
            )}
            {isTrainingsLoading && (
              <div className="-mx-5 grid grid-flow-col auto-cols-[286px] gap-3.5 overflow-x-auto px-5 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {Array.from({ length: 2 }).map((_, index) => (
                  <div
                    key={index}
                    className="h-[168px] animate-pulse rounded-[24px] bg-[#F1F2F1]"
                  />
                ))}
              </div>
            )}
            {!isTrainingsLoading && workoutPreviewItems.length > 0 && (
              <div className="scroll-reveal-list -mx-5 grid grid-flow-col auto-cols-[286px] gap-3.5 overflow-x-auto px-5 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {workoutPreviewItems.map((training) => (
                  <WorkoutCard
                    key={training.id_workout}
                    training={training}
                    onOpen={openWorkoutDetails}
                    t={t}
                  />
                ))}
              </div>
            )}
            <AnimatePresence>
              {isWorkoutMascotVisible && (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-y-0 right-0 z-10 flex translate-y-3 items-center"
                >
                  <motion.div
                    initial={{
                      opacity: 0,
                      x: prefersReducedMotion ? 0 : 12,
                    }}
                    animate={{ opacity: 0.28, x: 0 }}
                    exit={{
                      opacity: 0,
                      x: prefersReducedMotion ? 0 : 8,
                    }}
                    transition={{ duration: 0.18, ease: "easeOut" }}
                    className="size-[72px] overflow-hidden rounded-[12px]"
                  >
                    <img
                      src={WORKOUT_MASCOT_GIF_URL}
                      alt=""
                      className="size-full object-contain grayscale brightness-[1.45] [mask-image:linear-gradient(180deg,#000_0%,#000_68%,rgba(0,0,0,0)_100%)]"
                    />
                  </motion.div>
                </div>
              )}
            </AnimatePresence>
          </section>

          {shouldShowStartedPrograms && (
            <ProgramSection
              title={t("main:programs.startedTitle")}
              programs={startedProgramPreviewItems}
              isLoading={isStartedProgramTemplatesLoading}
              t={t}
              onProgramOpen={handleProgramOpen}
            />
          )}

          {shouldShowAvailablePrograms && (
            <ProgramSection
              title={t("main:programs.title")}
              programs={availableProgramPreviewItems}
              isLoading={isAvailableProgramTemplatesLoading}
              emptyLabel={t("main:programs.empty")}
              t={t}
              onProgramOpen={handleProgramOpen}
            />
          )}
        </main>
      </Content>
    </DefaultPage>
  );
});
